<template>
  <div
    :class="{
      'cursor-not-allowed':
        kpiTracker.selectedPerson.value === 0 || isCheckingPlayLimit,
      'opacity-50': kpiTracker.selectedPerson.value === 0 || isCheckingPlayLimit,
    }"
  >
    <button
      class="w-full py-3 text-sm font-bold text-center text-white uppercase rounded-xl bg-jm_orange play-button"
      @click="handleButtonClick"
      :disabled="kpiTracker.selectedPerson.value === 0 || isCheckingPlayLimit"
      :class="{ 'animate-play-button': animate }"
    >
      {{ $t("age_check.play_button") }}
    </button>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted } from "vue";
import { useGlobalStore } from "../stores/global";
import { useGameTracking } from "@/composables/useGameTracking";
import { checkAndConsumeDailyPlay } from "@/services/pimPlayLimit";

const globalStore = useGlobalStore();
const { kpiTracker, session } = useGameTracking();
const animate = ref(false);
const isCheckingPlayLimit = ref(false);

const handleButtonClick = async () => {
  if (kpiTracker.selectedPerson.value === 0 || isCheckingPlayLimit.value) {
    return;
  }

  if (!globalStore.legalConditionsAccepted) {
    globalStore.changeStep("termsAcceptance");
    return;
  }

  isCheckingPlayLimit.value = true;

  try {
    const campaignId =
      new URL(location.href).searchParams.get("campaignId") ?? "";
    const playLimitResult = await checkAndConsumeDailyPlay({
      participantKey: session.fingerprint.value || undefined,
      campaignId,
    });
    globalStore.setRemainingDailyPlays(playLimitResult.remainingPlays);
    globalStore.setShouldShowDailyLimitScreen(
      playLimitResult.remainingPlays === 0
    );

    if (!playLimitResult.allowed) {
      globalStore.setTimesLeftToPlay(0);
      globalStore.changeStep("thatsEnough");
      return;
    }
  } catch (error) {
    console.error("[PlayButton] Error while checking PIM play limit:", error);
    globalStore.setShouldShowDailyLimitScreen(false);
  } finally {
    isCheckingPlayLimit.value = false;
  }

  globalStore.setRaffleResult(null);

  // Start tracking game play time from the moment user clicks Play button
  kpiTracker.setGamePlayTimeStart(Date.now());

  // Set progress to 2 (game_started) when user clicks Play button
  // This triggers game_started_count++ on the backend
  kpiTracker.setProgress(2);

  // Update session with progress "game_started" to trigger game_started_count++
  console.log("[PlayButton] Updating session with progress 'game_started' (game_started_count will be incremented by backend)");
  try {
    await session.updateSessionProgress(kpiTracker.getKpiData(), { step: "game_started" });
    console.log("[PlayButton] Session update completed");
  } catch (error) {
    console.error("[PlayButton] Error updating session:", error);
  }
  
  globalStore.changeStep("gameMove");
};

onMounted(() => {
  setTimeout(() => {
    animate.value = true;
  }, 500);
});
</script>

<style lang="scss" scoped>
@import "@/assets/sass/main.sass";

.play-button {
  opacity: 0;
  transform: translateY(500px);
  width: 40vw;
  transition: transform 1s ease, opacity 1s ease;
}

.animate-play-button {
  opacity: 1 !important;
  transform: translateY(0) !important;
}
</style>
